<script lang="ts" setup>
import { computed } from 'vue';
import { Button } from '@laam/ui/base';
import type { ImageWithTextData } from '@laam/cms-shared';
import { useWindowScroll, watchDebounced, watchOnce } from '@vueuse/core';

interface Props {
	data: ImageWithTextData['output'];
	sectionIndex: number;
}

const { y } = useWindowScroll();
const hasWindowScrolled = ref(false);
watchOnce(y, () => {
	hasWindowScrolled.value = true;
});

const { $cathodeClient, $eventClient } = useNuxtApp();

const { data, sectionIndex } = defineProps<Props>();

const flexDirection = computed(() =>
	data.alignment === 'left' ? 'md:flex-row' : 'md:flex-row-reverse',
);

const isVisible = ref(false);

const normalizeLAAMUrls = (url: string) => {
	if (url.includes('https://laam.pk')) {
		return url.replace('https://laam.pk', '');
	}
	return url;
};

watchDebounced(
	isVisible,
	(value) => {
		if (value) {
			$cathodeClient.queueImpression('entity_impression', {
				data: [
					{
						href: normalizeLAAMUrls(data.link.href),
						data_string: JSON.stringify(data),
						title: data.link.name ?? '',
						image: data?.url ?? '',
					},
				],
				vertical_index: sectionIndex!,
				method: 'static',
				entity: '',
				heading: '',
				component: 'image-with-text',
				scroll_position: y.value,
				source: window.location.href,
				type: hasWindowScrolled.value ? 'scroll_impression' : 'load_impression',
				time: new Date().toUTCString(),
				data_source: 'manual',
				clicked_from: 'homepage',
				page: 'homepage',
			});
		}
	},
	{
		debounce: useRuntimeConfig().public.impressionDebounceTime,
	},
);

function handleClickEvent() {
	// Cathode event
	$cathodeClient.queueEvent('entity_click', {
		source: normalizeLAAMUrls(window.location.href),
		page: 'homepage',
		href: data.link.href,
		clicked_from: 'homepage',
		image: data.url ?? '',
		title: data.link.name ?? '',
		entity: '',
		horizontal_index: 0,
		vertical_index: sectionIndex!,
		slotted: false,
		heading: '',
		component: 'image-with-text',
		product_count: null,
		sale_percentage: 0,
		scroll_position: y.value,
		data_source: 'manual',
		data_string: JSON.stringify(data),
	});
	// statsig event
	$eventClient.sendEvent('homepage-entity-click', {
		to: data.link.href,
		index: sectionIndex!,
		title: '',
		component: 'image-with-text',
		entity: 'image-with-text',
		heading: '',
		horizontalIndex: 0,
		url: window.location.href,
	});
}
</script>

<template>
	<div
		:class="[
			'flex flex-col w-full md:h-[500px] md:rounded-sm overflow-hidden',
			flexDirection,
		]"
	>
		<!-- Image Side -->
		<div class="flex bg-gray-100 items-center h-[65%] md:h-full md:w-1/2">
			<NuxtImg
				:src="data.url"
				alt="Banner"
				class="w-full h-full object-cover"
			/>
		</div>

		<!-- Text Block -->
		<div class="flex items-center h-[35%] md:h-full md:w-1/2">
			<div
				class="flex flex-col px-10xl py-md items-start space-y-xl gap-4 w-fit md:w-full"
			>
				<div class="md:w-full">
					<h1
						class="text-xl md:text-2xl font-semibold max-w-[90%] text-start break-words"
					>
						{{ data.header || 'header' }}
					</h1>
					<p class="text-sm text-gray-600 text-start">
						{{ data.description || 'Optional description can be added.' }}
					</p>
				</div>
				<div>
					<NuxtLink
						:to="normalizeLAAMUrls(data.link.href)"
						as="button"
						:disabled="data.link.href === 'https://laam.pk'"
						@click="handleClickEvent()"
					>
						<Button variant="primary" class="mt-2 px-8 py-3 text-sm">
							{{ data.button_label || 'Explore collection' }}
						</Button>
					</NuxtLink>
				</div>
			</div>
		</div>
	</div>
</template>
